import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import TopNav from '../../components/layout/TopNav';
import { uploadVideoChunked } from '../../services/chunkedUpload';
import { useUploadStore } from '../../store';
import FormError from '../../components/shared/FormError';
import { autoCapFirst } from '../../utils/strings';

/* ---------- schema ---------- */
const uploadSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  buyerFirstName: z.string().optional(),
  buyerLastName: z.string().optional(),
  vin: z.string().optional(),
});
type UploadForm = z.infer<typeof uploadSchema>;

type DropState = 'idle' | 'hovering';

const ACCEPTED_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
const MAX_SIZE = 100 * 1024 * 1024; // 100 MB

const VideoUploadPage: React.FC = () => {
  const navigate = useNavigate();

  const [dropState, setDropState] = useState<DropState>('idle');
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { startUpload, setUploadProgress, confirmUpload, failUpload } = useUploadStore();

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm<UploadForm>({ resolver: zodResolver(uploadSchema) });

  /* ---------- drag / drop ---------- */
  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDropState('hovering');
  }, []);

  const onDragLeave = useCallback(() => {
    if (dropState === 'hovering') setDropState('idle');
  }, [dropState]);

  const validateAndSet = useCallback(
    (f: File) => {
      if (!ACCEPTED_TYPES.includes(f.type)) {
        toast.error('Only MP4, MOV, AVI or WebM files are accepted.');
        return;
      }
      if (f.size > MAX_SIZE) {
        toast.error('File exceeds 100 MB limit.');
        return;
      }
      setFile(f);
      setDropState('idle');

      const currentTitle = getValues('title');
      if (!currentTitle) {
        const nameWithoutExt = f.name.replace(/\.[^.]+$/, '');
        setValue('title', nameWithoutExt, { shouldValidate: true });
      }
    },
    [getValues, setValue],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const f = e.dataTransfer.files[0];
      if (f) validateAndSet(f);
    },
    [validateAndSet],
  );

  const onFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (f) validateAndSet(f);
    },
    [validateAndSet],
  );

  /* ---------- upload — fires and returns immediately ---------- */
  const onSubmit = (data: UploadForm) => {
    if (!file) {
      toast.error('Please select a video file first.');
      return;
    }

    const clientId = crypto.randomUUID();
    startUpload(clientId, data.title);

    uploadVideoChunked(
      file,
      {
        title: data.title,
        buyerFirstName: data.buyerFirstName,
        vin: data.vin,
        mimeType: file.type,
        fileSizeBytes: file.size,
      },
      (pct) => setUploadProgress(clientId, pct),
    )
      .then(({ videoId }) => {
        confirmUpload(clientId, videoId);
      })
      .catch(() => {
        failUpload(clientId);
        toast.error('Upload failed. Please try again.');
      });

    // Reset form immediately — user can start another upload or navigate away
    // The chunked transfer continues in the background via the promise above.
    setFile(null);
    reset();
    setDropState('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';

    toast.success(`"${data.title}" upload started.`);
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-slate-100 transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        <div className="space-y-12">
          {/* Header */}
          <header className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary shadow-inner">
                 <span className="material-symbols-outlined text-[28px]">movie_edit</span>
              </div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-none">
                Upload <span className="text-primary">&amp;</span> Score
              </h1>
            </div>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base max-w-2xl leading-relaxed ml-16">
              Precision analysis for high-stakes automotive sales presentations.
              Technical scoring engine active.
            </p>
          </header>

          {/* Drop Zone */}
          <div className="group relative">
            <div
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`bg-white dark:bg-slate-900 border-2 border-dashed p-12 md:p-16 rounded-[2.5rem] flex flex-col items-center justify-center text-center transition-all duration-500 shadow-sm cursor-pointer
                ${dropState === 'hovering'
                  ? 'border-primary bg-primary/[0.02] dark:bg-primary/[0.05] scale-[1.01] shadow-2xl shadow-primary/5'
                  : 'border-slate-200 dark:border-slate-800 hover:border-primary/50 hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}
            >
              <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={onFileSelect} />

              <div className="mb-6 relative">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary group-hover:scale-110 transition-transform duration-500 shadow-inner">
                  <span className="material-symbols-outlined text-[36px] fill-current">upload_file</span>
                </div>
                <div className="absolute inset-0 bg-primary/20 blur-3xl rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white tracking-tight mb-2">
                {file ? file.name : 'Select or drag video host'}
              </h3>
              <p className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-[0.2em]">
                {file ? `${(file.size / (1024 * 1024)).toFixed(1)} MB · Ready` : 'MP4, MOV, or AVI · Max 100MB'}
              </p>
              {!file && (
                <button className="mt-8 h-10 px-6 rounded-xl font-bold bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:border-primary dark:hover:border-primary hover:text-primary transition-all shadow-sm dark:shadow-none cursor-pointer text-xs">
                  Manual Intake
                </button>
              )}
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <form id="upload-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest flex items-center gap-2 ml-1">
                     <span className="material-symbols-outlined text-[16px]">title</span>
                     Asset Identifier
                  </label>
                  <input
                    {...register('title')}
                    onChange={(e) => setValue('title', autoCapFirst(e.target.value), { shouldDirty: true })}
                    className={`w-full h-14 rounded-2xl px-6 font-bold text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 outline-none focus:ring-4 transition-all shadow-sm dark:shadow-none border ${
                      errors.title
                        ? 'bg-red-50/50 dark:bg-red-950/20 border-red-300 dark:border-red-900 focus:border-red-400 focus:ring-red-500/5'
                        : 'bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-primary/5'
                    }`}
                    placeholder="e.g. Walkaround - 2026 Model X"
                    aria-invalid={!!errors.title}
                  />
                  <FormError message={errors.title?.message} />
                </div>
                <div className="space-y-3">
                  <label className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest flex items-center gap-2 ml-1">
                     <span className="material-symbols-outlined text-[16px]">person_search</span>
                     Buyer Identity
                  </label>
                  <input
                    {...register('buyerFirstName')}
                    onChange={(e) => setValue('buyerFirstName', autoCapFirst(e.target.value), { shouldDirty: true })}
                    className="w-full h-14 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-6 font-bold text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all shadow-sm dark:shadow-none"
                    placeholder="First Name (Optional)"
                  />
                </div>
                <div className="space-y-3 md:col-span-2">
                  <label className="text-[10px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-widest flex items-center gap-2 ml-1">
                     <span className="material-symbols-outlined text-[16px]">qr_code</span>
                     Inventory VIN
                  </label>
                  <input
                    {...register('vin')}
                    className="w-full h-14 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl px-6 font-bold text-slate-900 dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all shadow-sm dark:shadow-none"
                    placeholder="Vehicle Identification Number (Optional)"
                  />
                </div>
              </div>
            </form>

            <footer className="flex flex-col justify-end md:flex-row gap-4 pt-4">
              <button
                type="submit"
                form="upload-form"
                className="px-8 h-11 rounded-2xl font-bold bg-primary text-white text-sm uppercase tracking-widest shadow-lg shadow-primary/20 dark:shadow-none hover:bg-primary/90 hover:scale-[1.01] active:scale-[0.99] transition-all cursor-pointer flex items-center justify-center gap-3"
              >
                <span className="material-symbols-outlined text-[20px]">science</span>
                Initiate AI Analysis
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-11 px-6 rounded-xl font-bold text-red-500 dark:text-red-400 bg-red-50/50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-950/40 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer flex items-center gap-2 shadow-sm"
              >
                Abort
              </button>
            </footer>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VideoUploadPage;
